import { setChannel, setCallInProgress, setCallType, setOnboardingCallEvent, setOnboardingTourSteps, setOnboardingDefaultTourPending, resetState } from '../features/channelSlice';
import { closeSidePanel, clearEmbedHistory } from '../features/sidePanelSlice';
import { clearNotifications } from '../features/notificationSlice';
import { openLiveModal, closeLiveModal, clearLiveEvents, setLiveVideo, setOnboardingMode, addEventToHistory } from '../features/liveSlice';

// Stage 0: tear down anything that might hold Pega/Constellation components.
// Pulled out so quit/story-load can run teardown without the onboarding replay tail.
const teardownDynamicSurfaces = (dispatch) => {
  dispatch(setOnboardingTourSteps(null));
  dispatch(setOnboardingDefaultTourPending(false));
  dispatch(closeLiveModal());
  dispatch({ type: 'channel/setEmployeePegaWebEmbedProps', payload: null });
  dispatch({ type: 'channel/setAppPegaWebEmbedProps', payload: null });
  dispatch(closeSidePanel());
  dispatch(clearLiveEvents());
  dispatch(setLiveVideo(null));
};

const replayOnboardingEvents = (dispatch, onboardingEvents) => {
  if (!onboardingEvents || onboardingEvents.length === 0) {
    dispatch(setOnboardingCallEvent(null));
    dispatch(setOnboardingTourSteps(null));
    dispatch(setOnboardingDefaultTourPending(false));
    return;
  }

  const callEvent = onboardingEvents.find(e => e.type === 'call') || null;
  const tourEvent = onboardingEvents.find(e => e.type === 'tour') || null;
  const rawDefaultTourEvent = onboardingEvents.find(e => e.type === 'default-tour') || null;
  const tourAlreadyShown = localStorage.getItem('guidedTourShown') === 'true';
  const defaultTourEvent = (rawDefaultTourEvent && rawDefaultTourEvent.once && tourAlreadyShown)
    ? null
    : rawDefaultTourEvent;
  const mediaEvents = onboardingEvents.filter(
    e => e.type !== 'call' && e.type !== 'tour' && e.type !== 'default-tour'
  );

  dispatch(setOnboardingCallEvent(callEvent));
  dispatch(setOnboardingDefaultTourPending(!!defaultTourEvent));

  const tourSteps = tourEvent?.steps?.length > 0 ? tourEvent.steps : null;

  if (mediaEvents.length > 0) {
    const firstEvent = mediaEvents[0];
    dispatch(setOnboardingMode(true));
    dispatch(setLiveVideo({ ...firstEvent, isOnboardingEvent: true, immediate: true }));
    setTimeout(() => {
      // Open the modal and set the tour steps in the same dispatch batch so
      // GuidedTour never sees a window where steps are queued but the modal
      // isn't yet open — otherwise the nudge briefly renders against an empty
      // background before the LiveModal appears.
      dispatch(openLiveModal());
      dispatch(setOnboardingTourSteps(tourSteps));
      if (mediaEvents.length > 1) {
        setTimeout(() => {
          mediaEvents.slice(1).forEach(event => {
            dispatch(addEventToHistory({ ...event, isOnboardingEvent: true }));
          });
        }, 100);
      }
    }, 200);
  } else if (callEvent) {
    dispatch(setOnboardingTourSteps(tourSteps));
    const requestedType = callEvent.callType || 'voice';
    dispatch(setCallType(requestedType));
    dispatch(setCallInProgress(true));
    dispatch(setChannel('call'));
  } else if (defaultTourEvent) {
    // Joyride blocks the nudge via the `run` flag, so it's safe to queue
    // tour steps alongside the default tour kickoff.
    dispatch(setOnboardingTourSteps(tourSteps));
    setTimeout(() => { window.startGuidedTour?.(); }, 500);
  } else {
    // Pure-tour onboarding (no media, no call, no default tour) — nudge will
    // render immediately. Nothing to gate against; just set the steps.
    dispatch(setOnboardingTourSteps(tourSteps));
  }
};

// Full reset matching the "Reset" button: tear down surfaces, wait for Pega
// components to fully unmount, clear state, then replay onboarding events.
// Returns a Promise that resolves once the reset sequence is complete.
export const resetExperience = ({ dispatch, onboardingEvents, skipOnboardingReplay = false } = {}) => {
  console.log('🔄 RESET: Starting reset sequence...');

  teardownDynamicSurfaces(dispatch);

  return new Promise((resolve) => {
    setTimeout(() => {
      console.log('🔄 RESET: Stage 1 - Navigating to home');
      dispatch(setChannel('home'));
      dispatch(clearEmbedHistory());
    }, 200);

    setTimeout(() => {
      console.log('🔄 RESET: Stage 2 - Clearing state');
      dispatch({ type: 'channel/clearDynamicState' });
      dispatch(clearNotifications());
      dispatch(resetState());

      window.dispatchEvent(new CustomEvent('pega-conversation-clear', { detail: 'reset' }));

      if (!skipOnboardingReplay) {
        replayOnboardingEvents(dispatch, onboardingEvents);
      }
      resolve();
    }, 700);
  });
};

// Lighter variant for quit / story-switch callers that will run their own
// initialization afterwards: tears down surfaces and clears state but does
// NOT replay onboarding events (the caller is responsible for that).
export const teardownExperience = ({ dispatch } = {}) =>
  resetExperience({ dispatch, skipOnboardingReplay: true });
